import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import LoadingPage from '../ui/LoadingPage';

interface ProtectedRouteProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

/**
 * ProtectedRoute component that guards authenticated routes
 * - Redirects unauthenticated users to login page
 * - Preserves intended destination for post-login redirect
 * - Shows loading spinner while session is being restored
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  fallback 
}) => {
  const { isAuthenticated, isSessionRestored, isLoading, isGuest } = useAuth();
  const location = useLocation();

  // Show loading spinner while session is being restored
  if (!isSessionRestored || isLoading) {
    return (
      <LoadingPage 
        title="Loading..." 
        subtitle="Restoring your session" 
      />
    );
  }

  // If not authenticated (and not a guest), redirect to login with return URL
  // Guest users are considered authenticated for their session
  if (!isAuthenticated && !isGuest) {
    const returnUrl = location.pathname + location.search;
    return <Navigate to={`/auth/login?returnUrl=${encodeURIComponent(returnUrl)}`} replace />;
  }

  // Show custom fallback if provided
  if (fallback) {
    return <>{fallback}</>;
  }

  // User is authenticated, render children
  return <>{children}</>;
};

export default ProtectedRoute;
